const SearchStorageKeys = {
  'recently-viewed-vehicles': 'recently-viewed-vehicles',
  usedCarsPreviousUrl: 'usedCarsPreviousUrl',
  newCarsPreviousUrl: 'newCarsPreviousUrl',
} as const

type SearchStorageKeys =
  (typeof SearchStorageKeys)[keyof typeof SearchStorageKeys]

export type SearchMode = 'used' | 'new'

export const DEFAULT_SEARCH_MODE_URLS: Record<SearchMode, string> = {
  used: '/used-cars?financeType=any',
  new: '/new-cars',
}

const SEARCH_MODE_STORAGE_KEY: Record<SearchMode, SearchStorageKeys> = {
  used: SearchStorageKeys.usedCarsPreviousUrl,
  new: SearchStorageKeys.newCarsPreviousUrl,
}

const LOCALHOST_SEARCH_MODE_PORTS: Record<SearchMode, string> = {
  used: '4200',
  new: '4001',
}

const normaliseLocalhostSearchModeUrl = (
  searchMode: SearchMode,
  url: string,
) => {
  if (typeof window === 'undefined') {
    return url
  }

  try {
    const resolvedUrl = new URL(url, window.location.origin)

    if (resolvedUrl.hostname !== 'localhost') {
      return url
    }

    resolvedUrl.port = LOCALHOST_SEARCH_MODE_PORTS[searchMode]
    return resolvedUrl.toString()
  } catch {
    return url
  }
}

const getDefaultSearchModeUrl = (searchMode: SearchMode) => {
  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost'
  ) {
    return `${window.location.protocol}//${window.location.hostname}:${LOCALHOST_SEARCH_MODE_PORTS[searchMode]}${DEFAULT_SEARCH_MODE_URLS[searchMode]}`
  }

  return DEFAULT_SEARCH_MODE_URLS[searchMode]
}

export const setSessionStorage = (
  storageKey: SearchStorageKeys,
  value: string,
) => {
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem(storageKey, value)
  }
}

export const getSessionStorage = <T = unknown>(
  storageKey: SearchStorageKeys,
): T => {
  const storageValue = window.sessionStorage.getItem(storageKey)
  return typeof storageValue === 'string' ? JSON.parse(storageValue) : {}
}

export const setSearchModeLastActiveUrl = (
  searchMode: SearchMode,
  url: string,
) => {
  setSessionStorage(SEARCH_MODE_STORAGE_KEY[searchMode], url)
}

export const setCurrentSearchModeUrl = (searchMode: SearchMode) => {
  if (typeof window !== 'undefined') {
    setSearchModeLastActiveUrl(searchMode, window.location.href)
  }
}

export const getSearchModeLastActiveUrl = (searchMode: SearchMode) => {
  if (typeof window !== 'undefined') {
    const storedUrl = window.sessionStorage.getItem(
      SEARCH_MODE_STORAGE_KEY[searchMode],
    )

    if (storedUrl) {
      return normaliseLocalhostSearchModeUrl(searchMode, storedUrl)
    }

    return getDefaultSearchModeUrl(searchMode)
  }

  return getDefaultSearchModeUrl(searchMode)
}
